import BookCreate from "./BookCreate";
import { useParams, useLocation } from "react-router-dom";
import { useCategoriesData, useGetBook, useGetBookCategories, useGetBookVersions } from "@/hooks/api";
import { Navigation } from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

function apiDateToBR(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  const parts = dateStr.split("-");
  if (parts.length !== 3) return "";
  const [year, month, day] = parts;
  return `${day}/${month}/${year}`;
}

export default function BookEdit() {
  const { id = "" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const sectionParam = new URLSearchParams(location.search).get("section") as
    | "identificacao"
    | "categorias"
    | "versoes"
    | null;

  const bookId = parseInt(id, 10);
  const { data, isLoading, isError } = useGetBook(bookId);
  const { data: versionsData, isLoading: versionsLoading } = useGetBookVersions(bookId);
  const { data: categoriesData, isLoading: bookCategoriesLoading } = useGetBookCategories(bookId);
  const { data: categoriesCatalog } = useCategoriesData();

  const extractVersions = () => {
    const raw = versionsData?.data;
    if (Array.isArray(raw)) return raw;
    if (raw && typeof raw === "object") return [raw];
    return [];
  };

  const extractBookCategories = () => {
    const raw = categoriesData?.data;
    if (Array.isArray(raw)) return raw;
    if (raw && typeof raw === "object" && Array.isArray(raw.categories)) {
      return raw.categories;
    }
    return [];
  };

  if (isLoading || versionsLoading || bookCategoriesLoading) {
    return (
      <div className="flex min-h-screen bg-white">
        <Navigation />
        <div className="flex-1 flex flex-col bg-white">
          <div className="flex items-center h-[69px] px-6 border-b border-[#BAC9CA]">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/catalog")}
              className="w-12 h-12 rounded-full hover:bg-[#0000000D]"
            >
              <ArrowLeft className="w-6 h-6 text-[#3B494A]" />
            </Button>
            <h1 className="flex-1 text-[22px] leading-7 text-[#151C1D]">Editar Livro</h1>
          </div>
          <div className="flex-1 flex items-center justify-center">
            <span className="text-sm text-[#6B797A]">Carregando...</span>
          </div>
        </div>
      </div>
    );
  }

  if (isError || !data?.data) {
    return (
      <div className="flex min-h-screen bg-white">
        <Navigation />
        <div className="flex-1 flex flex-col bg-white">
          <div className="flex items-center h-[69px] px-6 border-b border-[#BAC9CA]">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/catalog")}
              className="w-12 h-12 rounded-full hover:bg-[#0000000D]"
            >
              <ArrowLeft className="w-6 h-6 text-[#3B494A]" />
            </Button>
            <h1 className="flex-1 text-[22px] leading-7 text-[#151C1D]">Editar Livro</h1>
          </div>
          <div className="flex-1 flex items-center justify-center">
            <span className="text-sm text-[#BA1A1A]">Livro não encontrado.</span>
          </div>
        </div>
      </div>
    );
  }

  const book = data.data;

  const contractType =
    book.contract_type === "determinate"
      ? "Determinado"
      : book.contract_type === "indeterminate"
      ? "Indeterminado"
      : "";

  const versions = extractVersions();
  const firstVersion = versions[0];
  const apiCategories = extractBookCategories();
  const mappedCategories = apiCategories
    .map((cat, index) => {
      const categoryId = Number(cat.category_id);
      const categoryFromCatalog = categoriesCatalog?.find((c) => Number(c.id) === categoryId);
      if (!categoryFromCatalog) return null;
      const pivotId = cat.id != null ? Number(cat.id) : undefined;
      return {
        id: `cat-${categoryId}-${index}`,
        categoryCode: categoryFromCatalog.code,
        bookCategoryId: pivotId != null && !Number.isNaN(pivotId) ? pivotId : undefined,
        csat: cat.csat == null ? "" : String(cat.csat),
        withoutNote: !!cat.is_new,
        isMain: !!cat.is_main,
      };
    })
    .filter((c): c is NonNullable<typeof c> => c !== null);

  const bookVersionIdFromApi =
    firstVersion?.id != null && !Number.isNaN(Number(firstVersion.id))
      ? Number(firstVersion.id)
      : undefined;

  return (
    <BookCreate
      headerTitle="Editar Livro"
      openSection={sectionParam ?? undefined}
      prefill={{
        bookId: String(book.id),
        bookVersionId: bookVersionIdFromApi,
        title: book.title ?? "",
        isbn: firstVersion?.isbn ? String(firstVersion.isbn) : "",
        sankhyaCode: firstVersion?.erp_code ? String(firstVersion.erp_code) : "",
        materialidade: book.materiality?.name ?? "",
        cartonadoSubtype: "",
        categories: mappedCategories,
        cost: book.cost ?? 0,
        contractType,
        contractExpiryDate: apiDateToBR(book.contract_expiration),
        contractStatus: "",
      }}
    />
  );
}
